import {getHours, isSameDay, parseISO} from "date-fns";
import {formatInTimeZone, toZonedTime} from "date-fns-tz";
import {enGB} from "date-fns/locale/en-GB";
import HighchartsReact from "highcharts-react-official";

import Highcharts from "@/components/Highcharts.tsx";

import styles from "./PublicProfilesReportedIssues.module.scss";

export const OUTAGE_THRESHOLD = 10;

export interface PublicProfilesReportedIssuesProps {
  timestamps: string[];
  start: Date;
  end: Date;
  timezone: string;
}

export const PublicProfilesReportedIssues = ({timestamps, start, end, timezone}: PublicProfilesReportedIssuesProps) => {
  const options = {
    title: {
      text: undefined,
    },
    credits: {
      enabled: false,
    },
    chart: {
      zooming: {
        type: "x",
      },
      time: {
        timezone: timezone,
      },
    },
    tooltip: {
      shared: true,
      outside: true,
      formatter: function (this: {
        points: {
          x: number;
          x2: number;
          y: number;
          color: string;
        }[];
        series: {
          name: string;
        };
      }) {
        const {points, series} = this;
        const point = points[0];
        if (point.y === 0) {
          return false;
        }
        const binStart = new Date(point.x);
        const binStartInTimezone = toZonedTime(binStart, timezone);
        const binEnd = new Date(point.x2);
        const binEndInTimezone = toZonedTime(binEnd, timezone);
        const sameDay = isSameDay(binStartInTimezone, binEndInTimezone);
        const sameHalfDay = getHours(binStartInTimezone) < 12 === getHours(binEndInTimezone) < 12;
        const parsedStart = formatInTimeZone(binStart, timezone, `MMM d, yyyy h:mm${sameHalfDay ? "" : "a"}`, {
          locale: enGB,
        });
        const parsedEnd = formatInTimeZone(binEnd, timezone, `${sameDay ? "" : `MMM d, yyyy `}h:mma z`, {locale: enGB});
        return `<span style="font-size: 0.8em">${parsedStart}-${parsedEnd}</span><br/><span style="color:${point.color}">●</span> ${series.name} <b>${point.y}</b><br/>`;
      },
    },
    xAxis: [
      {
        visible: false, // Needed to show xAxis when there is 1 histogram bucket
      },
      {
        type: "datetime",
        labels: {
          formatter: ({value}: {value: number}) => formatInTimeZone(new Date(value), timezone, "ha z", {locale: enGB}),
        },
        min: start.getTime(),
        max: end.getTime() + 60 * 1000, // 1 minute buffer to include recent report submissions
      },
    ],
    yAxis: {
      title: {
        text: undefined,
      },
      allowDecimals: false,
      startOnTick: true,
      min: 0,
      max: timestamps.length === 0 ? 2 * OUTAGE_THRESHOLD : undefined,
    },
    legend: {
      enabled: true,
      events: {
        itemClick: () => false,
      },
      accessibility: {
        enabled: false, // required to remove the pointer cursor
      },
      itemStyle: {
        cursor: "default",
      },
    },
    series:
      timestamps.length === 0
        ? [
            {
              name: "No issues reported",
              color: styles.empty,
              lineWidth: 0,
              marker: {
                symbol: "circle",
              },
            },
          ]
        : [
            {
              data: timestamps.map((t) => parseISO(t).getTime()),
              visible: false,
              showInLegend: false,
              enableMouseTracking: false,
            },
            {
              name: "Reported Issues",
              type: "histogram",
              baseSeries: 0,
              binWidth: 15 * 60 * 1000,
              showInLegend: false,
              zones: [
                {
                  value: OUTAGE_THRESHOLD,
                  color: styles.normal,
                },
                {
                  color: styles.outage,
                },
              ],
              xAxis: 1,
            },
            {
              name: "Detected Anomalies",
              color: styles.normal,
              lineWidth: 0,
              marker: {
                symbol: "circle",
              },
            },
            {
              name: "Outage Reported",
              color: styles.outage,
              lineWidth: 0,
              marker: {
                symbol: "circle",
              },
            },
          ],
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};
